/**
 * Runtime objects for direct and embedded doc ref eligible entity types.
 * This file is MANUALLY MAINTAINED.
 *
 * 1. Source of Truth: These objects are the source of truth for doc ref eligibility.
 *    Types in docRefEligibleEntityTypes.ts are derived from them using keyof typeof.
 *
 * 2. Code Generation: When you modify this file, run `yarn higharc gen doc-ref-eligible-entity-types`
 *    to regenerate the following files and commit the updates:
 *    - docRefIndirectEligibleEntities.ts (based on parent-child relationships and FK constraint
 *      analysis from docEntityConstraints.ts)
 *    - docRefIneligibleEntities.ts (the complement of direct/indirect/embedded)
 *    - docRefReverseTraversalTargetEntities.ts (reverse-traversal targets for doc refs)
 *
 * 3. CI Verification: `higharc verify doc-ref-entities` runs in the CI typecheck workflow
 *    and fails if the committed generated files don't match what the generator would produce.
 *
 * Adding a New Direct Eligible Entity Type
 *
 * Simply add the entity type to docRefDirectEligibleEntities below. The TypeScript compiler
 * will validate that it's a valid DocEntityType. After saving, run:
 *
 *   yarn genDocRefEligibleEntityTypes
 *
 * Understanding Eligibility Categories
 *
 * - Direct Eligible: Top-level entity types that can be explicitly selected for doc refs.
 *   These are manually curated here.
 *
 * - Indirect Eligible: Entity types that are included in doc refs because:
 *   - Their parent type is direct or indirect eligible, OR
 *   - They are referenced with STOP_AT constraints from eligible entities
 *   These are computed automatically via yarn genDocRefEligibleEntityTypes.
 *
 * - Embedded: Entity types that are serialized inline within their parent entity,
 *   rather than having separate table entries. Manually curated here.
 *
 * - Ineligible: All remaining entity types that are not included in doc refs.
 *   Computed automatically as the complement.
 *
 * @see ./DocRefEligibleEntityTypes.md for a guided overview of how the full
 * direct/indirect/ineligible generation system works.
 */
import type { DocEntityType } from "../doc/docEntityTypes";

export const docRefDirectEligibleEntities = {
  CONTENT_TEMPLATE: true,
  COMPONENT: true,
  DATA_SEQUENCE: true,
  DATUM: true,
  ELEVATION: true,
  LABEL: true,
  FACADE: true,
  FOUNDATION_ASSEMBLY: true,
  FOUNDATION_COMPONENT: true,
  FRAME_FLOOR_ASSEMBLY: true,
  FRAMING_COMPONENT: true,
  GLOBAL_PARAMETER: true,
  GROUP: true,
  HEADER_ASSEMBLY: true,
  MODIFIER: true,
  CIP_COMPONENT: true,
  MASONRY_COMPONENT: true,
  OPTION_LOCK: true,
  OPTION_RULE: true,
  OPTION_SET: true,
  PALETTE: true,
  PLAN_SET_TEMPLATE: true,
  PRESET: true,
  PROFILE: true,
  PROP: true,
  ROOM_LAYOUT: true,
  RULE_BASED_SELECTOR: true,
  SETTINGS_SET: true,
  SHEATHING_COMPONENT: true,
  SHEET: true,
  SITE: true,
  SLAB_COMPONENT: true,
  SLAB_FLOOR_ASSEMBLY: true,
  STAIR_SETTING: true,
  VIEW_TEMPLATE: true,
  VIEW_TEMPLATE_OVERRIDE: true,
  WALL_PLANE_ASSEMBLY: true,
  WALL_PLANE_FILTER: true,
} as const satisfies Partial<Record<DocEntityType, true>>;

export const embeddedDocRefEligibleEntities = {
  CATALOG_ITEM: true,
  ELECTRICAL_RULE: true,
  OPTION: true,
  ROOF_SIDE: true,
} as const satisfies Partial<Record<DocEntityType, true>>;
